// Admin "Tasks" page: the coach dashboard's Task Tracker (same component,
// "admin" variant) over every Balgruha. Task names are plain text here — no
// details popup; editing goes through the row's pencil (Edit Task). The
// Assignee filter lists every non-student user ("All Users"), while the
// assignee dropdowns keep the assignable-for-schedule list. The old board
// has no button here any more but still opens from /task?view=board.
// Other roles keep the board as before (see App.js).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import TaskTracker from '../../components/dashboard/TaskTracker';
import TaskManagement from '../../components/TaskManagement/taskmanagement';
import useFixedHeaderOverlap from '../../components/adminDailySchedule/useFixedHeaderOverlap';
import { getAssignableTaskUsers, getAssignableUsersForSchedule, getBalagruha } from '../../api';
import showToast from '../../utils/toast';
import '../../components/adminTasks/AdminTaskTracker.css';

export default function AdminTaskTracker() {
  const [searchParams, setSearchParams] = useSearchParams();
  const showBoard = searchParams.get('view') === 'board';
  const pageRef = useRef(null);
  const headerOverlap = useFixedHeaderOverlap(pageRef);

  const [balagruhas, setBalagruhas] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUsers, setFilterUsers] = useState(undefined);

  useEffect(() => {
    getBalagruha()
      .then((response) => setBalagruhas(response?.data?.balagruhas || []))
      .catch(() => showToast('Failed to load Balgruhas', 'error'));
    getAssignableUsersForSchedule()
      .then((response) => setUsers(response?.data || []))
      .catch(() => showToast('Failed to load assignees', 'error'));
    // Every non-student user, for the Assignee filter. If it fails the
    // filter falls back to the assignee list above.
    getAssignableTaskUsers()
      .then((response) => setFilterUsers(response?.data?.staff || undefined))
      .catch(() => {});
  }, []);

  // Stable between renders so the tracker only refetches when it changes.
  const balagruhaIds = useMemo(() => balagruhas.map((balagruha) => balagruha._id), [balagruhas]);

  const showTracker = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next);
  };

  if (showBoard) {
    return (
      <div ref={pageRef} className="att-board" style={{ '--att-header-overlap': `${headerOverlap}px` }}>
        <button type="button" className="att-switch att-back" onClick={showTracker}>
          <ArrowLeft size={16} aria-hidden="true" /> Task Tracker
        </button>
        <TaskManagement />
      </div>
    );
  }

  return (
    <div ref={pageRef} className="att-page" style={{ '--att-header-overlap': `${headerOverlap}px` }}>
      <TaskTracker
        variant="admin"
        selectedBalagruhas={balagruhaIds}
        balagruhas={balagruhas}
        users={users}
        assigneeFilterUsers={filterUsers}
        canCreate
        canUpdate
      />
    </div>
  );
}
